import { applyPageMetadata } from "@/lib/page-metadata";
import { formatPageDate, getPageDate } from "@/data/page-dates";
import { LastUpdated } from "@/components/site/LastUpdated";
import { createFileRoute, notFound, Link, redirect } from "@tanstack/react-router";
import React from "react";
import { useEffect, useState } from "react";
import { Layout, Section, Eyebrow, FinalCTA, Breadcrumbs, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { getBlogPost, blogPosts, BLOG_AUTHOR, type BlogPost } from "@/data/blog-posts";
import { CaseStudy } from "@/components/site/CaseStudy";
import { FAQSection } from "@/components/site/FAQSection";
import { BLOG_VISUALS } from "@/data/blog-listing";
import { ArticleBody, extractH2s, inline } from "@/components/blog/ArticleBody";
import AiVisibilityCheck from "@/components/blog/AiVisibilityCheck";
import LawFirmMarketingRoi from "@/components/blog/LawFirmMarketingRoi";
import { CHATGPT_CALLOUT, CHATGPT_RELATED, CHATGPT_IMAGE_ALT } from "@/data/posts/how-law-firms-get-found-on-chatgpt";

function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const endEl = document.getElementById("blog-content-end");
      const vh = window.innerHeight;
      // Document-space bottom of the measured content (through final CTA).
      const endBottomDoc = endEl
        ? endEl.getBoundingClientRect().bottom + window.scrollY
        : document.documentElement.scrollHeight;
      // Full scrollable range: from the very top of the page to when the
      // content bottom reaches the viewport bottom. Starts at the first scroll.
      const total = Math.max(1, endBottomDoc - vh);
      const entered = window.scrollY;
      const pct = Math.min(100, Math.max(0, (entered / total) * 100));
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  const pct = Math.round(progress);
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] h-6 bg-[var(--navy-deep)]/90 backdrop-blur-sm border-t border-[var(--gold)]/30 flex items-center pointer-events-none animate-fade-in overflow-hidden">
      <div
        className="h-full transition-[width] duration-150 ease-out flex items-center justify-end pr-3"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg, var(--gold), var(--gold-deep))",
          boxShadow: "0 0 10px var(--gold)",
          minWidth: pct === 0 ? 0 : "3.5rem",
        }}
      >
        {pct > 0 && pct < 100 && (
          <span className="text-sm font-extrabold text-[var(--navy-deep)] tabular-nums whitespace-nowrap" style={{ textShadow: "0 0 6px rgba(255,255,255,0.6)" }}>
            {pct}%
          </span>
        )}
      </div>
      {pct >= 100 && (
        <span className="absolute right-3 text-sm font-extrabold text-[var(--gold)] tabular-nums" style={{ textShadow: "0 0 8px rgba(0,0,0,0.8)" }}>100%</span>
      )}
      {pct === 0 && (
        <span className="absolute left-4 text-sm font-bold text-[var(--gold)] tabular-nums" style={{ textShadow: "0 0 8px rgba(0,0,0,0.8)" }}>0%</span>
      )}
    </div>
  );
}

// Anchor-text internal linking map. First case-insensitive match of each phrase
// (per post) is wrapped in an <a> to the target URL. Order matters, longer/
// more specific phrases first so they win over shorter overlapping phrases.
const LINK_MAP: { phrase: string; to: string }[] = [
  { phrase: "personal injury lawyer seo", to: "/practice-areas/personal-injury-lawyer-seo" },
  { phrase: "personal injury seo", to: "/practice-areas/personal-injury-lawyer-seo" },
  { phrase: "personal injury", to: "/practice-areas/personal-injury-lawyer-seo" },
  { phrase: "dui / dwi", to: "/practice-areas/dui-dwi-lawyer-seo" },
  { phrase: "dui/dwi", to: "/practice-areas/dui-dwi-lawyer-seo" },
  { phrase: "dui lawyer seo", to: "/practice-areas/dui-dwi-lawyer-seo" },
  { phrase: "dui defense", to: "/practice-areas/dui-dwi-lawyer-seo" },
  { phrase: "dui", to: "/practice-areas/dui-dwi-lawyer-seo" },
  { phrase: "criminal defense", to: "/practice-areas/criminal-defense-lawyer-seo" },
  { phrase: "family law", to: "/practice-areas/family-law-lawyer-seo" },
  { phrase: "estate planning", to: "/practice-areas/estate-planning-lawyer-seo" },
  { phrase: "immigration law", to: "/practice-areas/immigration-lawyer-seo" },
  { phrase: "local seo for law firms", to: "/services/local-seo-for-law-firms" },
  { phrase: "local seo", to: "/services/local-seo-for-law-firms" },
  { phrase: "google business profile", to: "/services/local-seo-for-law-firms" },
  { phrase: "gbp", to: "/services/local-seo-for-law-firms" },
  { phrase: "link building", to: "/services/link-building-for-law-firms" },
  { phrase: "editorial links", to: "/services/link-building-for-law-firms" },
  { phrase: "on-page seo", to: "/blog/on-page-seo-for-lawyers" },
  { phrase: "technical seo", to: "/services/technical-seo-for-law-firms" },
  { phrase: "content marketing", to: "/services/legal-content-writing" },
  { phrase: "schema", to: "/services/technical-seo-for-law-firms" },
  { phrase: "law firm seo services", to: "/services" },
  { phrase: "seo services", to: "/services" },
  { phrase: "ai seo & geo", to: "/services/ai-seo-geo" },
  { phrase: "ai seo and geo", to: "/services/ai-seo-geo" },
  { phrase: "generative engine optimization", to: "/services/ai-seo-geo" },
  { phrase: "ai citation", to: "/services/ai-seo-geo" },
  { phrase: "free seo audit", to: "/law-firm-seo-audit" },
  { phrase: "seo audit", to: "/law-firm-seo-audit" },
  { phrase: "case studies", to: "/case-studies" },
  { phrase: "pricing", to: "/pricing" },
  { phrase: "transparent pricing", to: "/pricing" },
  { phrase: "pdf guides", to: "/guides" },
  { phrase: "pdf version", to: "/guides" },
  { phrase: "law firm seo strategy", to: "/blog/law-firm-seo-strategy" },
  { phrase: "law firm seo agency", to: "/blog/how-to-choose-law-firm-seo-agency" },
];

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function slugifyHeading(h: string) {
  return h
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function linkifyParagraph(text: string, used: Set<string>, currentPath: string, enhanced = false): React.ReactNode[] {
  // Explicit markdown-style links: [anchor](url). External URLs open in a new tab.
  const mdRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  if (mdRe.test(text)) {
    mdRe.lastIndex = 0;
    const out: React.ReactNode[] = [];
    let cursor = 0;
    let m: RegExpExecArray | null;
    let k = 0;
    while ((m = mdRe.exec(text))) {
      if (m.index > cursor) out.push(...linkifyParagraph(text.slice(cursor, m.index), used, currentPath, enhanced));
      const external = /^https?:\/\//.test(m[2]);
      out.push(
        <a
          key={`md-${k++}`}
          href={m[2]}
          {...(external ? { target: "_blank", rel: "noopener" } : {})}
          className="text-[var(--gold-deep)] font-medium underline decoration-[var(--gold)] decoration-2 underline-offset-4 transition-colors"
        >
          {m[1]}
        </a>,
      );
      cursor = m.index + m[0].length;
    }
    if (cursor < text.length) out.push(...linkifyParagraph(text.slice(cursor), used, currentPath, enhanced));
    return out;
  }
  // Build list of matches: earliest index wins, then longest phrase.
  type Match = { start: number; end: number; to: string; text: string };
  const matches: Match[] = [];
  const taken: Array<[number, number]> = [];
  const overlaps = (s: number, e: number) => taken.some(([a, b]) => s < b && e > a);

  for (const { phrase, to } of LINK_MAP) {
    if (used.has(to) || (to === currentPath && phrase !== "on-page seo")) continue;
    const re = new RegExp(`\\b${escapeRe(phrase)}\\b`, "i");
    const m = re.exec(text);
    if (!m) continue;
    const start = m.index;
    const end = start + m[0].length;
    if (overlaps(start, end)) continue;
    matches.push({ start, end, to, text: m[0] });
    taken.push([start, end]);
    used.add(to);
  }
  if (matches.length === 0) return [text];

  const linkClass = enhanced
    ? "text-[var(--gold-deep)] font-medium underline decoration-[var(--gold)] decoration-2 underline-offset-4 hover:text-[var(--gold-deep)] hover:decoration-[var(--gold-deep)] hover:decoration-2 transition-colors"
    : "text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary transition";

  matches.sort((a, b) => a.start - b.start);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) out.push(text.slice(cursor, m.start));
    out.push(
      <a
        key={`lnk-${i}-${m.to}`}
        href={m.to}
        className={linkClass}
      >
        {m.text}
      </a>,
    );
    cursor = m.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function TableOfContents({ sections }: { sections: { h: string }[] }) {
  return (
    <nav aria-label="Table of contents" className="rounded-xl border border-border bg-secondary/50 p-6">
      <div className="text-lg font-bold uppercase tracking-wider text-[var(--gold-deep)] border-b border-border pb-3 mb-4">
        Table of contents
      </div>
      <ol className="space-y-2.5">
        {sections.map((sec, idx) => (
          <li key={sec.h} className="flex items-start gap-3">
            <span className="flex-shrink-0 text-sm font-semibold tabular-nums leading-snug text-[var(--gold-deep)]">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <a
              href={`#${slugifyHeading(sec.h)}`}
              title={sec.h}
              className="min-w-0 text-sm font-medium leading-snug text-foreground/80 hover:text-[var(--gold-deep)] transition-colors line-clamp-2"
            >
              {sec.h}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params, location }) => {
    const retired: Record<string, string> = {
      "local-seo-guide-for-law-firms": "/services/local-seo-for-law-firms",
      "personal-injury-lawyer-seo-guide": "/practice-areas/personal-injury-lawyer-seo",
      "law-firm-seo-what-lawyers-need-to-know": "/services/law-firm-seo",
      "law-firm-seo-serp-features": "/services/law-firm-seo",
      "law-firm-seo-guide": "/services/law-firm-seo",
    };
    const slugKey = params.slug.replace(/\/+$/, "");
    if (retired[slugKey]) {
      throw redirect({ href: retired[slugKey] + (location.searchStr ?? ""), statusCode: 301 });
    }
    const moved: Record<string, string> = {
      "generative-engine-optimization-geo-law-firms-2026": "top-geo-agencies-for-law-firms",
    };
    if (moved[params.slug]) {
      throw redirect({ to: "/blog/$slug", params: { slug: moved[params.slug] }, statusCode: 301 });
    }
    const post = getBlogPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.post;
    if (!p) return { meta: [{ title: "Blog post" }] };
    const title = p.seoTitle ?? p.title;
    const description = p.metaDescription ?? p.excerpt;
    const author = p.author ?? BLOG_AUTHOR;
    const socialImage = BLOG_VISUALS[p.slug]?.image;
    const absoluteSocialImage = socialImage
      ? socialImage.startsWith("https://")
        ? socialImage
        : `https://pageonelawyers.com${socialImage}`
      : undefined;
    return applyPageMetadata(`/blog/${params.slug}`, {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "author", content: author.name },
        { name: "keywords", content: p.targetKeyword },
        { name: "article:published_time", content: p.publishedAt },
        { name: "article:modified_time", content: p.updatedAt },
        { name: "article:author", content: author.name },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `https://pageonelawyers.com/blog/${params.slug}` },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(absoluteSocialImage
          ? [
              { property: "og:image", content: absoluteSocialImage },
              { name: "twitter:image", content: absoluteSocialImage },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: `https://pageonelawyers.com/blog/${params.slug}` }],
      scripts: p.structuredData ? p.structuredData.map((schema) => ({ type: "application/ld+json", children: JSON.stringify(schema) })) : p.body ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                "@id": `https://pageonelawyers.com/blog/${params.slug}#article`,
                headline: p.title,
                description: p.excerpt,
                datePublished: p.publishedAt,
                dateModified: p.updatedAt,
                mainEntityOfPage: `https://pageonelawyers.com/blog/${params.slug}`,
                author: { "@type": "Person", name: BLOG_AUTHOR.name, url: "https://pageonelawyers.com/about" },
                publisher: { "@type": "Organization", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://pageonelawyers.com/" },
                  { "@type": "ListItem", position: 2, name: "Blog", item: "https://pageonelawyers.com/blog" },
                  { "@type": "ListItem", position: 3, name: p.shortTitle, item: `https://pageonelawyers.com/blog/${params.slug}` },
                ],
              },
            ],
          }),
        },
      ] : [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            datePublished: p.publishedAt,
            dateModified: p.updatedAt,
            author: {
              "@type": "Person",
              name: BLOG_AUTHOR.name,
              jobTitle: BLOG_AUTHOR.role,
              description: BLOG_AUTHOR.bio,
              url: BLOG_AUTHOR.url,
              image: `https://pageonelawyers.com${BLOG_AUTHOR.image}`,
            },
            publisher: {
              "@type": "Organization",
              name: "PageOne Lawyers",
              url: "https://pageonelawyers.com",
            },
            mainEntityOfPage: `https://pageonelawyers.com/blog/${params.slug}`,
            keywords: p.targetKeyword,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "/blog" },
              { "@type": "ListItem", position: 3, name: p.shortTitle, item: `/blog/${params.slug}` },
            ],
          }),
        },
      ],
    });
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <Layout><Section><h1 className="text-3xl font-semibold text-foreground">Post not found</h1></Section></Layout>
  ),
  errorComponent: () => (
    <Layout><Section><h1 className="text-3xl text-foreground">Something went wrong</h1></Section></Layout>
  ),
});

function BlogPostPage() {
  const { post: p } = Route.useLoaderData() as { post: BlogPost };
  const others = blogPosts.filter((x) => x.slug !== p.slug).slice(0, 6);
  const publishedFmt = formatPageDate(getPageDate(`/blog/${p.slug}`)?.publishedAt ?? p.publishedAt.split("T")[0]);
  const currentPath = `/blog/${p.slug}`;
  const usedLinks = new Set<string>(["dui-lawyer-seo-guide", "best-law-firm-seo-tools"].includes(p.slug) ? ["/practice-areas/dui-dwi-lawyer-seo"] : []);
  const enhancedLinks = true;
  const tocSections = p.body ? extractH2s(p.body) : p.sections;
  const author = p.author ?? BLOG_AUTHOR;

  return (
    <Layout>
      <ScrollProgress />
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Blog", href: "/blog" }, { label: p.shortTitle }]} />
          <Eyebrow><span className="text-[var(--gold)]">Guide</span></Eyebrow>
          <h1 className="mt-6 font-display text-4xl md:text-5xl font-semibold leading-tight text-primary-foreground">{p.title}</h1><LastUpdated />
          {!p.strictEditorial && <p className="mt-6 text-xl text-primary-foreground/85">{p.excerptMarkdown || p.slug === "law-firm-seo-tips" ? inline(p.excerpt, "post-intro") : p.excerpt}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-primary-foreground/80">
            <span>By <Link to={author.url} className="font-semibold text-[var(--gold)] hover:underline">{author.name}</Link></span>
            <span>·</span>
            <span>Published {publishedFmt}</span>
            <span>·</span>
            <span>{p.readMinutes} min read</span>
          </div>
        </div>
      </section>

      <Section className="!max-w-[88rem]">
        <div className={p.strictEditorial ? "mx-auto max-w-4xl" : "lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-12"}>
        <article id="blog-article" className="min-w-0 space-y-12">
          {p.body && (
            <ArticleBody body={p.body} extendedMarkdown={p.extendedMarkdown} plainTableBody={["social-media-scheduling-tools-for-law-firms", "law-firm-performance-metrics"].includes(p.slug)} afterFirstSection={p.strictEditorial ? undefined : <TableOfContents sections={tocSections} />} componentOverrides={["how-to-generate-leads-for-law-firms", "how-to-measure-roi-on-law-firm-marketing"].includes(p.slug) ? { LawFirmMarketingRoi: <LawFirmMarketingRoi ctaHref="/law-firm-seo-audit" ctaText="Get a free tracking audit →" /> } : p.sourceExact ? { AiVisibilityCheck: <AiVisibilityCheck ctaHref="/law-firm-seo-audit" /> } : undefined} templateBlocks={{
              ChatGptCallout: p.sourceExact && <div className="rounded-lg border-l-4 border-border bg-secondary/50 p-6"><p className="text-xl leading-relaxed text-foreground/85">{inline(CHATGPT_CALLOUT, "chatgpt-callout")}</p></div>,
              ChatGptRelated: p.sourceExact && <div className="rounded-lg border-l-4 border-border bg-secondary/50 p-6"><p className="text-lg leading-relaxed text-foreground">{inline(CHATGPT_RELATED, "chatgpt-related")}</p></div>,
              ChatGptImage: p.sourceExact && <img src="/images/blog/how-law-firms-get-found-on-chatgpt/how-to-get-law-firm-cited-by-chatgpt.webp" alt={CHATGPT_IMAGE_ALT} width={1200} height={675} loading="lazy" decoding="async" className="h-auto w-full" />,
              RelatedReading: p.relatedLinks[0] && (
                <div className="rounded-xl border-l-4 border-[var(--gold)] bg-secondary/50 p-6">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">Related reading</div>
                  <Link to={p.relatedLinks[0].to} className="mt-2 block text-lg font-semibold text-foreground hover:text-primary">{p.relatedLinks[0].label} →</Link>
                  <p className="mt-1 text-sm text-muted-foreground">See how this connects to your firm's practice-area strategy.</p>
                </div>
              ),
              FreeAudit: (
                <div className="rounded-xl bg-[var(--navy-deep)] text-primary-foreground p-8">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold)]">Free audit</div>
                  <h3 className="mt-2 text-2xl font-semibold">Want us to apply this to your firm?</h3>
                  <p className="mt-2 text-primary-foreground/80">Get a free SEO audit, we'll pinpoint exactly where {p.shortTitle.toLowerCase()} is costing you cases.</p>
                  <div className="mt-5"><CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton></div>
                </div>
              ),
              GoDeeper: p.relatedLinks[1] && (
                <div className="rounded-xl border-l-4 border-primary bg-secondary/50 p-6">
                  <div className="text-xs uppercase tracking-wider font-semibold text-primary">Go deeper</div>
                  <Link to={p.relatedLinks[1].to} className="mt-2 block text-lg font-semibold text-foreground hover:text-primary">{p.relatedLinks[1].label} →</Link>
                </div>
              ),
              PdfGuides: (
                <div className="rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">Download</div>
                    <div className="mt-1 text-lg font-semibold text-foreground">Grab the PDF version of this guide.</div>
                    <div className="text-sm text-muted-foreground">Free, no email required.</div>
                  </div>
                  <CTAButton to="/guides" variant="gold">Browse PDF guides</CTAButton>
                </div>
              ),
            }} />
          )}
          {!p.body && p.intro && (
            <div className="space-y-5">
              {p.intro.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-xl text-foreground/85 leading-relaxed">
                  {linkifyParagraph(para, usedLinks, currentPath, enhancedLinks)}
                </p>
              ))}
            </div>
          )}
          {!p.body && p.sections.map((s, i) => (
            <div key={s.h}>
              <h2 id={slugifyHeading(s.h)} className="scroll-mt-28 text-3xl font-semibold text-foreground mb-4">{s.h}</h2>
              <div className="space-y-5">
                {(Array.isArray(s.p) ? s.p : [s.p]).map((paragraph, paragraphIndex) => (
                  <p key={`${s.h}-p-${paragraphIndex}`} className="text-xl text-foreground/85 leading-relaxed">
                    {linkifyParagraph(paragraph, usedLinks, currentPath, enhancedLinks)}
                  </p>
                ))}
                {s.image && (
                  <figure className="overflow-hidden rounded-lg border border-border bg-card">
                    <div className="aspect-[1200/628] bg-secondary/40">
                      <img
                        src={s.image.src}
                        alt={s.image.alt}
                        width={1200}
                        height={628}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </figure>
                )}
                {s.table && (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[720px] border-collapse text-left text-[15px]">
                      <thead className="bg-primary text-primary-foreground">
                        <tr>
                          {s.table.headers.map((h) => (
                            <th key={h} scope="col" className="px-4 py-3 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, ri) => (
                          <tr key={ri} className={ri % 2 ? "bg-muted/40" : "bg-card"}>
                            {row.map((cell, ci) => (
                              <td key={ci} className={`border-t border-border px-4 py-3 align-top text-foreground/85 ${ci === 0 ? "font-semibold text-foreground" : ""}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {s.bullets && (
                  <ul className="space-y-4 pl-6 text-xl text-foreground/85 leading-relaxed list-disc marker:text-[var(--gold-deep)]">
                    {s.bullets.map((bullet, bulletIndex) => (
                      <li key={`${s.h}-bullet-${bulletIndex}`}>
                        {linkifyParagraph(bullet, usedLinks, currentPath, enhancedLinks)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {i === 0 && p.sections.length > 2 && (
                <div className="mt-8 lg:hidden">
                  <TableOfContents sections={p.sections} />
                </div>
              )}
              {i === 1 && p.relatedLinks[0] && (
                <div className="mt-8 rounded-xl border-l-4 border-[var(--gold)] bg-secondary/50 p-6">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">Related reading</div>
                  <a href={p.relatedLinks[0].to} className="mt-2 block text-lg font-semibold text-foreground hover:text-primary">
                    {p.relatedLinks[0].label} →
                  </a>
                  <p className="mt-1 text-sm text-muted-foreground">See how this connects to your firm's practice-area strategy.</p>
                </div>
              )}
              {i === 3 && (
                <div className="mt-8 rounded-xl bg-[var(--navy-deep)] text-primary-foreground p-8">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold)]">Free audit</div>
                  <h3 className="mt-2 text-2xl font-semibold">Want us to apply this to your firm?</h3>
                  <p className="mt-2 text-primary-foreground/80">Get a free SEO audit, we'll pinpoint exactly where {p.shortTitle.toLowerCase()} is costing you cases.</p>
                  <div className="mt-5"><CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton></div>
                </div>
              )}
              {i === 4 && p.relatedLinks[1] && (
                <div className="mt-8 rounded-xl border-l-4 border-primary bg-secondary/50 p-6">
                  <div className="text-xs uppercase tracking-wider font-semibold text-primary">Go deeper</div>
                  <a href={p.relatedLinks[1].to} className="mt-2 block text-lg font-semibold text-foreground hover:text-primary">
                    {p.relatedLinks[1].label} →
                  </a>
                </div>
              )}
              {i === p.sections.length - 2 && (
                <div className="mt-8 rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">Download</div>
                    <div className="mt-1 text-lg font-semibold text-foreground">Grab the PDF version of this guide.</div>
                    <div className="text-sm text-muted-foreground">Free, no email required.</div>
                  </div>
                  <CTAButton to="/guides" variant="gold">Browse PDF guides</CTAButton>
                </div>
              )}
            </div>
          ))}
        </article>
        {!p.strictEditorial && tocSections.length > 2 && (
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <TableOfContents sections={tocSections} />
              <a href="/law-firm-seo-audit" className="block group">
                <div className="relative overflow-hidden rounded-xl bg-[var(--navy-deep)] p-6 text-center shadow-xl ring-1 ring-[var(--gold)]/30 transition-transform group-hover:scale-[1.03] group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
                  <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 30% 15%, var(--gold) 0%, transparent 55%), linear-gradient(160deg, transparent 40%, rgba(0,0,0,0.3) 100%)" }} />
                  <div className="absolute -top-px left-0 right-0 h-1 bg-gold-gradient" />
                  <div className="relative">
                    <div className="absolute -top-1 right-0 rounded-full bg-[var(--gold)] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--navy-deep)] shadow">Free</div>
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[var(--gold)]/30 to-[var(--gold-deep)]/20 ring-2 ring-[var(--gold)]/50 shadow-inner">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                      </svg>
                    </div>
                    <div className="text-xs uppercase tracking-wider font-bold text-[var(--gold)]">No cost · No obligation</div>
                    <h3 className="mt-2 text-lg font-bold text-primary-foreground leading-snug">Get your free law firm SEO audit</h3>
                    <p className="mt-2 text-sm text-primary-foreground/75">See exactly what's costing you cases and how to fix it in 7 days.</p>
                    <ul className="mt-3 space-y-1 text-xs text-primary-foreground/70 text-left mx-auto max-w-[200px]">
                      <li className="flex gap-1.5"><span className="text-[var(--gold)]">✓</span> Keyword gap analysis</li>
                      <li className="flex gap-1.5"><span className="text-[var(--gold)]">✓</span> Competitor breakdown</li>
                      <li className="flex gap-1.5"><span className="text-[var(--gold)]">✓</span> 90-day action plan</li>
                    </ul>
                    <span className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gold-gradient px-5 py-2.5 text-xs font-extrabold text-[var(--navy-deep)] shadow-md group-hover:brightness-110 transition-all">
                      Get my free audit
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </aside>
        )}
        </div>

        {(p.author && !["how-to-use-technology-to-improve-legal-marketing", "how-to-generate-leads-for-law-firms", "how-to-measure-roi-on-law-firm-marketing"].includes(p.slug) ? <aside className="mt-16 border-t border-border py-8"><Link to={p.author.url} className="text-xl font-semibold text-foreground underline decoration-border underline-offset-4">{p.author.name}</Link></aside> : <aside className="mt-16 rounded-xl border border-border bg-secondary/40 p-8">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-shrink-0">
              <img
                src={BLOG_AUTHOR.image}
                alt={`${BLOG_AUTHOR.name}, ${BLOG_AUTHOR.role}`}
                width={96}
                height={96}
                loading="lazy"
                className="w-24 h-24 rounded-full object-cover bg-secondary border border-border"
              />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">About the author</div>
              <div className="mt-2 text-xl font-semibold">
                <a
                  href={BLOG_AUTHOR.url}
                  className="text-foreground underline decoration-[var(--gold)] decoration-2 underline-offset-4 hover:text-[var(--gold-deep)] transition-colors"
                >
                  {BLOG_AUTHOR.name}
                </a>
              </div>
              <div className="text-sm text-muted-foreground">{BLOG_AUTHOR.role}</div>
              <p className="mt-3 text-foreground/85 leading-relaxed">{BLOG_AUTHOR.bio}</p>
              <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
                {BLOG_AUTHOR.credentials.map((c) => (
                  <li key={c} className="flex gap-2"><span className="text-[var(--gold-deep)]">✓</span>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </aside>)}

        {!p.strictEditorial && <div className="mt-16 rounded-xl border border-border bg-secondary/50 p-8 text-center">
          <h3 className="text-2xl font-semibold text-foreground">Want this applied to your firm?</h3>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Get a free SEO audit and we'll show you exactly which of these tactics your firm should ship first.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton>
            <CTAButton to={BOOKING_URL}>Book a strategy call</CTAButton>
          </div>
        </div>}
      </Section>

      <div id="blog-content-end">
      {p.caseStudy && <CaseStudy {...p.caseStudy} eyebrow="Case study" closingContent={p.caseStudy.closingCopy ? inline(p.caseStudy.closingCopy, "case-study-closing") : undefined} />}

      {p.faqs.length > 0 && (
        <FAQSection
          eyebrow="Frequently asked"
          title={`${p.shortTitle}, questions we hear most`}
          items={p.faqs}
          renderAnswer={p.slug === "law-firm-seo-strategy" ? (answer) => inline(answer, "strategy-faq") : undefined}
        />
      )}

       {(p.relatedLinks.length > 0 || ["how-to-generate-leads-for-law-firms", "how-to-measure-roi-on-law-firm-marketing"].includes(p.slug)) && <div className="bg-hero-gradient text-primary-foreground">
        <Section className="!max-w-5xl">
          <div className="text-center mb-8">
            <Eyebrow><span className="text-[var(--gold)]">Keep reading</span></Eyebrow>
            <h3 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-primary-foreground">Related resources</h3>
            <p className="mt-3 text-primary-foreground/75 max-w-xl mx-auto">Hand-picked guides and tools that connect to what you just read.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {(p.relatedLinks.length > 0 ? p.relatedLinks : [
              { label: "Law Firm Lead Generation", to: "/services/lead-generation-for-law-firms" },
              { label: "Law Firm Marketing Analytics", to: "/services/law-firm-marketing-analytics" },
            ]).map((l) => (
              <a
                key={l.to}
                href={l.to}
                className="group relative rounded-xl border border-[var(--gold)]/25 bg-white/[0.04] backdrop-blur-sm px-6 py-5 hover:border-[var(--gold)] hover:bg-white/[0.08] hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-between gap-4"
              >
                <span className="flex items-center gap-3 text-primary-foreground font-medium">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--gold)]/15 text-[var(--gold)] transition group-hover:bg-[var(--gold)]/25">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h16M14 6l6 6-6 6" /></svg>
                  </span>
                  {l.label}
                </span>
                <span className="text-[var(--gold)] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
              </a>
            ))}
          </div>
        </Section>
      </div>}

      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <h3 className="text-2xl font-semibold text-foreground mb-6">More guides</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to="/blog/$slug" params={{ slug: o.slug }} className="rounded-xl border border-border bg-background p-6 hover:border-[var(--gold)] transition">
                <div className="text-base font-semibold text-foreground">{o.shortTitle}</div>
                <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{o.excerpt}</div>
                <div className="mt-4 text-sm font-semibold text-primary">Read →</div>
              </Link>
            ))}
          </div>
        </Section>
      </div>

      <FinalCTA />
      </div>
    </Layout>
  );
}